import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";
import type { TransactionRow, TransactionType, TransactionStatus } from "@/types/database";

const PAGE_SIZE = 20;

interface Filter {
  type?: TransactionType;
  status?: TransactionStatus;
}

export function useTransactionsInfinite(filter: Filter = {}) {
  const { user } = useAuth();
  return useInfiniteQuery({
    queryKey: ["transactions", user?.id, filter],
    enabled: !!user?.id,
    initialPageParam: 0,
    getNextPageParam: (last: TransactionRow[], pages) =>
      last.length < PAGE_SIZE ? undefined : pages.length,
    queryFn: async ({ pageParam }) => {
      let q = supabase
        .from("transactions")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .range(pageParam * PAGE_SIZE, pageParam * PAGE_SIZE + PAGE_SIZE - 1);
      if (filter.type) q = q.eq("type", filter.type);
      if (filter.status) q = q.eq("status", filter.status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as TransactionRow[];
    },
  });
}

export function usePendingWithdrawal() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["withdrawal", "pending", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<TransactionRow | null> => {
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("user_id", user!.id)
        .eq("type", "withdraw")
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

/**
 * Dernier dépôt en attente avec un token PayDunya/Geniuspay associé.
 * Sert de filet de sécurité sur le wallet quand l'utilisateur ne repasse
 * jamais par /payment/return (ex: reste bloqué sur la page de paiement du
 * provider, ferme l'onglet et revient directement dans l'app).
 */
export function usePendingDeposit() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["deposit", "pending", user?.id],
    enabled: !!user?.id,
    refetchInterval: (q) => ((q.state.data as TransactionRow | null)?.id ? 5000 : false),
    queryFn: async (): Promise<TransactionRow | null> => {
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("user_id", user!.id)
        .eq("type", "deposit")
        .eq("status", "pending")
        .not("paydunya_token", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useTransaction(id: string | undefined) {
  return useQuery({
    queryKey: ["transaction", id],
    enabled: !!id,
    refetchInterval: (q) => {
      const tx = q.state.data as TransactionRow | undefined;
      return tx?.status === "pending" ? 3000 : false;
    },
    queryFn: async (): Promise<TransactionRow | null> => {
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}
